import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CoverageDetails } from 'components/CoverageDetails';
import { CoverageTableHeader } from 'components/CoverageTable/components/CoverageTableHeader';
import { currencyFormat } from 'helpers/utils';
import { isEmpty, isNumber } from 'lodash-es';
import theme from 'providers/theme/theme';
import React, { type FC, Fragment } from 'react';
import CoverageTableWCMessage from '../CoverageTableWCMessage/CoverageTableWCMessage';
import {
  CoverageTableMobileAdditionalDataStacksStyle,
  CoverageTableMobileStyle,
  CoverageTableMobileTopBar,
  CoverageTableMobileWCMessageRowStyle,
  CoverageTableMobileWorkersCompensationBody,
} from './CoverageTableMobile.styles';
import type {
  CoverageTableMobileBodyRow,
  CoverageTableMobileProps,
} from './CoverageTableMobile.types';

const renderTableBodyRow = (row: CoverageTableMobileBodyRow) => {
  if (isEmpty(row.deductible) && isEmpty(row.limit)) return null;

  const deductibleRows = row.deductible as { label?: string; value: string }[];
  const limitRows = row.limit as { label?: string; value: string }[];

  const maxRowsQuantity = Math.max(deductibleRows.length, limitRows.length);

  const tableRows: React.JSX.Element[] = [];

  for (let i = 0; i < maxRowsQuantity; i += 1) {
    const limit = limitRows[i];
    const deductible = deductibleRows[i];

    const tableRow = (
      <TableRow key={i}>
        {limit && (
          <TableCell align="right">
            <Box>
              <Typography variant="body1">
                {isNumber(limit.value)
                  ? currencyFormat(limit.value, undefined, true).merged
                  : limit.value}
              </Typography>

              <Typography variant="body2" color={theme.palette.customColors.darkGray}>
                {limit.label || ''}
              </Typography>
            </Box>
          </TableCell>
        )}

        {deductible && (
          <TableCell align="right">
            <Box>
              <Typography variant="body1">
                {isNumber(deductible.value)
                  ? currencyFormat(deductible.value, undefined, true).merged
                  : deductible.value}
              </Typography>

              <Typography variant="body2" color={theme.palette.customColors.darkGray}>
                {deductible.label || ''}
              </Typography>
            </Box>
          </TableCell>
        )}
      </TableRow>
    );

    tableRows.push(tableRow);
  }

  return tableRows;
};

const CoverageTableMobile: FC<CoverageTableMobileProps> = ({ tablesData, quoteId }) => (
  <>
    <Box sx={CoverageTableMobileTopBar}>Quote Number {quoteId}</Box>
    {tablesData?.map((tableData, index) => (
      <Fragment key={tableData.title}>
        <CoverageTableHeader tableData={tableData} isFirstHeader={index === 0} />

        {tableData?.rows?.map((row) => (
          <Table key={row.id} sx={[CoverageTableMobileStyle]}>
            <TableHead>
              {row.id === 'workers_compensation_message' ? (
                <TableRow sx={CoverageTableMobileWCMessageRowStyle}>
                  <TableCell>
                    <CoverageTableWCMessage
                      messageVariant={row.additionalValues?.messageVariant}
                      isMobile
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  <TableRow>
                    <TableCell colSpan={2} align="left">
                      {row.coverages && <CoverageDetails {...row.coverages} />}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="h3">Limit</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="h3">Deductible</Typography>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableHead>
            <TableBody
              sx={
                row.id === 'workers_compensation' ? CoverageTableMobileWorkersCompensationBody : {}
              }
            >
              {renderTableBodyRow({
                deductible: row.deductible,
                limit: row.limit,
              })}

              {!isEmpty(row.additionalData) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Stack sx={CoverageTableMobileAdditionalDataStacksStyle}>
                      {row.additionalData?.map((item) => (
                        <CoverageDetails key={item.listKey || item.triggerText} {...item} />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ))}
      </Fragment>
    ))}
  </>
);
export default CoverageTableMobile;
